import FileDownload from "@mui/icons-material/FileDownload";
import { IconButton, ListItemText, Menu, MenuItem, Tooltip } from "@mui/material";
import { GridSortModel } from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ViolationExportShape } from "encamp-shared/src/violations/violationExportColumns";
import { mergeColumnVisibility } from "hooks/mergedColumnVisibility";
import {
  gridSettingsStorageKey,
  type GridPersistenceSettings,
} from "hooks/omnisearch-v2/useGridPersistence";
import { useExportData } from "hooks/useExportData";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { useCallback, useMemo, useState } from "react";

import { VIOLATION_DEFAULT_COLUMN_VISIBILITY, VIOLATIONS_GRID_PERSISTENCE_KEY } from "./columns";

const BUTTON_ID = "violations-export-button";
const MENU_ID = "violations-export-menu";

/**
 * The menu's three shapes, least- to most-expanded; `secondary` is the short
 * description a modal would otherwise carry. Each item's `aria-label`
 * pins its accessible name to `primary`, since MUI's `ListItemText` otherwise
 * concatenates `primary` and `secondary` and two of these would then collide on
 * a startsWith/contains match.
 */
const EXPORT_OPTIONS: {
  shape: ViolationExportShape;
  primary: string;
  secondary: string;
}[] = [
  {
    shape: "grouped",
    primary: "One row per violation",
    secondary: "Each individual violation on its own row, how the grid displays",
  },
  {
    shape: "summary",
    primary: "One row per facility",
    secondary: "Each facility has its own row, includes unresolved & severity totals",
  },
  {
    shape: "flat",
    primary: "One row per violation type, per facility",
    secondary: "How many times each violation type has occurred at each facility",
  },
];
/**
 * STAFF-ONLY. The download route refuses a non-staff caller too; the gate lives
 * here rather than at the call site so no future toolbar can forget it.
 */
export function ViolationsExportMenu({
  tenantId,
  search,
  sortModel,
  isStaff,
}: {
  tenantId: string;
  search: string;
  sortModel: GridSortModel;
  isStaff: boolean;
}) {
  const alerts = useAlerts();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  // Violations change under a stable filter as users edit them, so a second
  // click must re-query rather than replay a cached file.
  const { startExport, status } = useExportData({ autoDownload: true, disableCache: true });
  // Read through the hook that writes them, so a change to the key or the shape
  // is a type error here.
  const [gridSettings] = useLocalStorageState<GridPersistenceSettings | null>(
    gridSettingsStorageKey(VIOLATIONS_GRID_PERSISTENCE_KEY),
    { defaultValue: null }
  );

  // The desktop slot, because the file is meant to mirror the desktop grid.
  // Persistence stores only what the user CHANGED, so the defaults have to be
  // merged back in.
  const columnVisibility = useMemo(
    () =>
      mergeColumnVisibility(
        VIOLATION_DEFAULT_COLUMN_VISIBILITY,
        gridSettings?.desktop?.columnVisibilityModel
      ),
    [gridSettings]
  );

  const runExport = useCallback(
    (shape: ViolationExportShape) => {
      setAnchorEl(null);
      alerts.success("Generating file...");
      const sort = sortModel
        .filter((s) => Boolean(s.sort))
        .map((s) => ({ field: s.field, sort: s.sort ?? "asc" }));
      startExport({
        exportType: "harmonizedViolations",
        tenantId,
        fileName: "violations",
        filter: {
          shape,
          // The grid renders `lastSeenAt` in this zone and the worker runs in
          // UTC, so without this the file would date an evening violation a day
          // later than the page it was exported from.
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          ...(search ? { search } : {}),
          ...(sort.length > 0 ? { sort } : {}),
        },
        columnVisibility,
      });
    },
    [alerts, startExport, tenantId, search, sortModel, columnVisibility]
  );

  // After the hooks above, which must run on every render.
  if (!isStaff) {
    return null;
  }

  return (
    <>
      <Tooltip title="Export">
        <IconButton
          id={BUTTON_ID}
          size="medium"
          color="info"
          aria-label="Export"
          aria-haspopup="true"
          aria-controls={open ? MENU_ID : undefined}
          aria-expanded={open ? "true" : undefined}
          loading={status === "loading"}
          onClick={(e) => setAnchorEl(e.currentTarget)}
        >
          <FileDownload />
        </IconButton>
      </Tooltip>
      <Menu
        id={MENU_ID}
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        slotProps={{
          list: { "aria-labelledby": BUTTON_ID },
          // Wide enough that the longest secondary line wraps instead of
          // stretching the menu to its length.
          paper: { sx: { maxWidth: 360 } },
        }}
      >
        {EXPORT_OPTIONS.map(({ shape, primary, secondary }) => (
          <MenuItem key={shape} aria-label={primary} onClick={() => runExport(shape)}>
            <ListItemText
              primary={primary}
              secondary={secondary}
              slotProps={{
                primary: { sx: { whiteSpace: "normal" } },
                secondary: { sx: { whiteSpace: "normal" } },
              }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
